import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { EuroInput } from "@/components/EuroInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, AlertTriangle } from "lucide-react";
import { PartyRows } from "@/components/PartyRows";
import { usePostalCity, formatCp } from "@/lib/postal";

const TYPES = ["Apartamento", "Moradia", "Terreno", "Loja", "Escritório", "Outro"];
const DETAILS = [["artigo", "Artigo matricial"], ["fracao", "Fração"], ["freguesia", "Freguesia"], ["areas", "Áreas"], ["titulares", "Titulares"]];
const KIND_TYPE = { apartamento: "Apartamento", moradia: "Moradia", terreno: "Terreno", loja: "Loja", escritorio: "Escritório" };

const Field = ({ label, children }) => <div><Label>{label}</Label><div className="mt-1.5">{children}</div></div>;

// «Rua … · 0000-000 Cidade» ↔ três campos (moradas antigas: «Rua …, Cidade»).
const splitAddress = (a = "") => {
  const [street, rest = ""] = a.split(" · ");
  const m = (rest || street).match(/(\d{4}-\d{3})\s*(.*)$/);
  if (m) return { street: (rest ? street : street.slice(0, m.index)).replace(/[,\s]+$/, ""), cp: m[1], city: m[2].trim() };
  if (rest) return { street, cp: "", city: rest.trim() };
  const parts = street.split(", ");
  return parts.length > 1 && !/\d/.test(parts[parts.length - 1]) ? { street: parts.slice(0, -1).join(", "), cp: "", city: parts[parts.length - 1] } : { street, cp: "", city: "" };
};
const joinAddress = (f) => {
  const tail = [f.cp.trim(), f.city.trim()].filter(Boolean).join(" ");
  return tail ? `${f.street.trim()} · ${tail}` : f.street.trim();
};

export const EditarDados = ({ p, onDone, trigger }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [sellers, setSellers] = useState([]);
  usePostalCity(f?.cp, f?.city, (v) => setF((o) => ({ ...o, city: v })));
  const start = () => {
    const m = p.mediation || {};
    setF({
      property_ref: p.property_ref || "", ...splitAddress(p.property_address || ""), property_type: p.property_type || KIND_TYPE[p.property_kind || p.property_kind_read] || "",
      is_fraction: !!p.is_fraction, construction_year: p.construction_year || "", mortgage_answer: p.mortgage_answer || "nao_sei",
      is_rented: !!p.is_rented, details: { ...(p.property_details || {}) },
      m: { signed_date: m.signed_date || "", duration_months: m.duration_months ?? "", exclusive: !!m.exclusive,
        asking_price: m.asking_price ?? "", commission_type: m.commission_type || "pct", commission_value: m.commission_value ?? "" },
    });
    setSellers((p.sellers || []).map((s) => ({ civil_status: "", email: "", phone: "", ...s })));
    setOpen(true);
  };
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = async () => {
    if (f.cp.trim() && !/^\d{4}-\d{3}$/.test(f.cp.trim())) { toast.error("Código postal no formato 0000-000."); return; }
    try {
      await api.put(`/processes/${p.id}`, {
        property_ref: f.property_ref, property_address: joinAddress(f), property_type: f.property_type || null,
        is_fraction: f.is_fraction, construction_year: num(f.construction_year), mortgage_answer: f.mortgage_answer,
        is_rented: f.is_rented, property_details: f.details, sellers: sellers.filter((s) => s.name),
        mediation: { ...f.m, signed_date: f.m.signed_date || null, duration_months: num(f.m.duration_months),
          asking_price: num(f.m.asking_price), commission_value: num(f.m.commission_value) },
      });
      toast.success("Dados guardados"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const s = (k, v) => setF({ ...f, [k]: v });
  const sm = (k, v) => setF({ ...f, m: { ...f.m, [k]: v } });
  return (
    <>
      {trigger ? trigger(start) : <button onClick={start} data-testid="edit-data-btn" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white text-ec-green hover:bg-slate-50 transition-colors">
        <Pencil size={14} /> Editar dados
      </button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-data-dialog">
          <DialogHeader><DialogTitle className="font-heading text-ec-green">Editar dados da angariação</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Referência do imóvel"><Input value={f.property_ref} onChange={(e) => s("property_ref", e.target.value)} data-testid="ed-ref" /></Field>
                <Field label="Morada"><Input value={f.street} onChange={(e) => s("street", e.target.value)} placeholder="Rua …, n.º …, andar" data-testid="ed-address" /></Field>
                <Field label="Código postal"><Input value={f.cp} onChange={(e) => s("cp", formatCp(e.target.value))} placeholder="0000-000" inputMode="numeric" data-testid="ed-postal-code" /></Field>
                <Field label="Cidade"><Input value={f.city} onChange={(e) => s("city", e.target.value)} placeholder="ex.: Lisboa" data-testid="ed-locality" /></Field>
                <Field label="Tipo">
                  <Select value={f.property_type} onValueChange={(v) => s("property_type", v)}>
                    <SelectTrigger data-testid="ed-type"><SelectValue placeholder="Escolher" /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Ano de construção"><Input type="number" value={f.construction_year} onChange={(e) => s("construction_year", e.target.value)} data-testid="ed-year" /></Field>
                {DETAILS.map(([k, l]) => (
                  <Field key={k} label={`${l}${p.property_details_read?.[k] ? ` · lido da ${p.property_details_read[k]}` : ""}`}><Input value={f.details[k] || ""} onChange={(e) => s("details", { ...f.details, [k]: e.target.value })} data-testid={`ed-${k}`} /></Field>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Contrato assinado"><DateInput value={f.m.signed_date} onChange={(e) => sm("signed_date", e.target.value)} data-testid="ed-signed" /></Field>
                <Field label="Duração (meses)"><Input type="number" value={f.m.duration_months} onChange={(e) => sm("duration_months", e.target.value)} data-testid="ed-duration" /></Field>
                <Field label="Preço pedido (€)"><EuroInput value={f.m.asking_price ?? ""} onChange={(v) => sm("asking_price", v)} data-testid="ed-price" /></Field>
                <Field label="Comissão">
                  <div className="flex gap-2">
                    <Select value={f.m.commission_type} onValueChange={(v) => sm("commission_type", v)}>
                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pct">%</SelectItem><SelectItem value="valor">€</SelectItem></SelectContent>
                    </Select>
                    {f.m.commission_type === "valor"
                      ? <EuroInput decimals value={f.m.commission_value ?? ""} onChange={(v) => sm("commission_value", v)} data-testid="ed-commission" />
                      : <Input type="number" step="0.01" inputMode="decimal" value={f.m.commission_value} onChange={(e) => sm("commission_value", e.target.value)} data-testid="ed-commission" />}
                  </div>
                </Field>
                <Field label="Tem hipoteca?">
                  <Select value={f.mortgage_answer} onValueChange={(v) => s("mortgage_answer", v)}>
                    <SelectTrigger data-testid="ed-mortgage"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sim">Sim</SelectItem><SelectItem value="nao">Não</SelectItem><SelectItem value="nao_sei">Não sei</SelectItem></SelectContent>
                  </Select>
                </Field>
                <div className="flex flex-col gap-3 pt-6">
                  <label className="flex items-center justify-between text-sm">Exclusividade <Switch checked={f.m.exclusive} onCheckedChange={(v) => sm("exclusive", v)} /></label>
                  <label className="flex items-center justify-between text-sm">Fração <Switch checked={f.is_fraction} onCheckedChange={(v) => s("is_fraction", v)} /></label>
                  <label className="flex items-center justify-between text-sm">Arrendado <Switch checked={f.is_rented} onCheckedChange={(v) => s("is_rented", v)} /></label>
                </div>
              </div>
              <div><Label>Proprietário(s)</Label><div className="mt-2"><PartyRows list={sellers} setList={setSellers} prefix="ed-seller" /></div></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={save} data-testid="ed-save" className="bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Conflicts = ({ p, onDone }) => {
  if (!p.field_conflicts?.length) return null;
  const resolve = async (field, use) => {
    await api.post(`/processes/${p.id}/conflicts/resolve`, { field, use });
    toast.success("Divergência resolvida"); onDone();
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-[16px] p-5 space-y-3" data-testid="field-conflicts">
      <h3 className="font-heading text-lg text-amber-900 flex items-center gap-2"><AlertTriangle size={18} /> Divergências para decidir</h3>
      {p.field_conflicts.map((c) => (
        <div key={c.field} className="bg-white rounded-xl border border-amber-100 p-3 text-sm" data-testid={`conflict-${c.field}`}>
          <div className="font-medium text-slate-800">{c.label}</div>
          <div className="text-slate-600 mt-1">{c.dictated ? "Ditado no WhatsApp" : "Escrito à mão"}: <b>{String(c.manual)}</b> · {c.doc_name}: <b>{String(c.doc_value)}</b></div>
          {c.note && <div className="text-xs text-amber-800 mt-1" data-testid={`conflict-note-${c.field}`}>{c.note}</div>}
          <div className="flex gap-2 mt-2">
            <button onClick={() => resolve(c.field, "manual")} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`conflict-keep-${c.field}`}>{c.dictated ? "Manter o ditado" : "Manter o valor escrito"}</button>
            <button onClick={() => resolve(c.field, "doc")} className="text-xs px-2.5 py-1.5 rounded-lg bg-ec-green text-white hover:bg-ec-greenDark" data-testid={`conflict-doc-${c.field}`}>Usar o valor do documento</button>
          </div>
        </div>
      ))}
    </div>
  );
};
